import { useState, useEffect } from 'react';
import { profileService } from '../services/profileService';
import { User } from '../types';
import { doc, setDoc, getDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../services/firebase';

export const useProfiles = () => {
  const [profiles, setProfiles] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      const profilesData = await profileService.fetchProfiles();
      setProfiles(profilesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch profiles');
    } finally {
      setLoading(false);
    }
  };

  const sendMatchRequest = async (senderId: string, receiverDocId: string, message: string, currentMatches: string[] = []) => {
    try {
      const senderRef = doc(db, 'users', senderId);
      const receiverRef = doc(db, 'users', receiverDocId);

      // Get receiver's current matches and uid
      const receiverDoc = await getDoc(receiverRef);
      if (!receiverDoc.exists()) {
        throw new Error('Receiver profile not found');
      }

      const receiverData = receiverDoc.data();
      const receiverMatches = receiverData.matches || [];
      const receiverUid = receiverData.uid;

      // Create match request document
      const matchRequestRef = doc(collection(db, 'matchRequests'));
      await setDoc(matchRequestRef, {
        senderId,
        receiverId: receiverUid,
        message,
        status: 'pending',
        timestamp: serverTimestamp()
      });

      // Update both users' matches arrays
      await Promise.all([
        setDoc(senderRef, {
          matches: [...(currentMatches || []), receiverUid]
        }, { merge: true }),
        
        setDoc(receiverRef, {
          matches: [...receiverMatches, senderId]
        }, { merge: true })
      ]);

    } catch (error) {
      console.error('Error sending match request:', error);
      throw error;
    }
  };

  useEffect(() => {
    fetchProfiles();
  }, []);

  return { profiles, loading, error, sendMatchRequest };
};